(() => {
  const SECTION_SEL = '[data-zerra-reasons]';

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function parseHex(hex) {
    if (!hex || typeof hex !== 'string') return null;
    let h = hex.trim().replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6) return null;
    const n = Number.parseInt(h, 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  function isDarkHex(hex) {
    const rgb = parseHex(hex);
    if (!rgb) return false;
    return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 < 148;
  }

  function applyTone(root, bg) {
    const dark = isDarkHex(bg);
    root.dataset.tone = dark ? 'dark' : 'light';
    root.style.setProperty('--zerra-reasons-bg', bg);
    root.style.setProperty('--zerra-reasons-fg', dark ? '#f4faf6' : '#0b100e');
    root.style.setProperty('--zerra-reasons-muted', dark ? 'rgba(244, 250, 246, 0.68)' : '#5b665f');
  }

  function init(root) {
    if (!root || root.dataset.reasonsReady === '1') return;
    root.dataset.reasonsReady = '1';

    const track = root.querySelector('[data-reasons-track]');
    const fill = root.querySelector('[data-reasons-fill]');
    const numEl = root.querySelector('[data-reasons-num]');
    const currentEl = root.querySelector('[data-reasons-current]');
    const panels = [...root.querySelectorAll('[data-reasons-panel]')];
    const dots = [...root.querySelectorAll('[data-reasons-dot]')];
    const arts = [...root.querySelectorAll('[data-reasons-art]')];
    const defaultBg = root.dataset.defaultBg || '#f5f7f6';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!track || panels.length === 0) return;

    const total = panels.length;
    let active = -1;
    let ticking = false;
    let pulseTimer = 0;

    const setActive = (index, progress) => {
      const i = Math.max(0, Math.min(total - 1, index));
      if (fill) fill.style.transform = `scaleX(${Math.max(0.02, Math.min(1, progress))})`;

      if (i === active) return;
      const prev = active;
      active = i;

      panels.forEach((panel, idx) => {
        const on = idx === i;
        panel.classList.toggle('is-active', on);
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      dots.forEach((dot, idx) => {
        const on = idx === i;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });

      arts.forEach((art) => {
        const idx = Number(art.dataset.artIndex);
        const on = idx === i;
        const was = idx === prev;
        art.classList.toggle('is-leaving', was && !on && prev >= 0);
        art.classList.toggle('is-active', on);
        if (on) {
          window.setTimeout(() => art.classList.remove('is-leaving'), 50);
        }
        if (!on && was) {
          window.setTimeout(() => art.classList.remove('is-leaving'), 700);
        }
      });

      const bg = panels[i]?.dataset.bg || defaultBg;
      applyTone(root, bg);

      const label = pad(i + 1);
      if (numEl) {
        numEl.textContent = label;
        if (!reduce) {
          numEl.classList.add('is-pulse');
          window.clearTimeout(pulseTimer);
          pulseTimer = window.setTimeout(() => numEl.classList.remove('is-pulse'), 420);
        }
      }
      if (currentEl) currentEl.textContent = label;
      root.style.setProperty('--zerra-reasons-active', String(i));
    };

    const update = () => {
      ticking = false;
      if (reduce) {
        setActive(0, 1);
        return;
      }

      const rect = track.getBoundingClientRect();
      const scrollable = Math.max(1, track.offsetHeight - window.innerHeight);
      const raw = -rect.top / scrollable;
      const progress = Math.min(1, Math.max(0, raw));
      const index = Math.min(total - 1, Math.floor(progress * total - 0.0001));
      setActive(Number.isFinite(index) ? index : 0, progress);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    const scrollToStep = (index) => {
      const i = Math.max(0, Math.min(total - 1, index));
      const scrollable = Math.max(1, track.offsetHeight - window.innerHeight);
      const trackTop = window.scrollY + track.getBoundingClientRect().top;
      const target = trackTop + scrollable * ((i + 0.5) / total);
      window.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' });
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = Number(dot.dataset.reasonsDot);
        if (!Number.isNaN(i)) scrollToStep(i);
      });
    });

    // initial tone from first panel
    applyTone(root, panels[0]?.dataset.bg || defaultBg);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    root._zerraReasonsTeardown = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.clearTimeout(pulseTimer);
      delete root.dataset.reasonsReady;
    };
  }

  function boot(scope) {
    (scope || document).querySelectorAll(SECTION_SEL).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll(SECTION_SEL).forEach((root) => {
      if (typeof root._zerraReasonsTeardown === 'function') root._zerraReasonsTeardown();
    });
  });
})();
